import { getMailsComponentsForTemplate, getVisibleMails } from "../components/contenu-mails";
import { CompletionModal } from "../components/completion-modal";
import { Layout } from "../components/layout/layout";
import { MailDetail } from "../components/mail/mail-detail";
import { MailRow } from "../components/mail/mail-row";
import { ScrollArea, Loader, Button } from "@repo/ui";
import { AVAILABLE_COLORS } from "../lib/constants";
import {
  isBackendError,
  StatutGlobalEnum,
  StatutMailEnum,
  type InbasketEvaluation,
  type MailComponent,
  type MailReponses,
} from "../lib/types";
import { estEvaluationSoumise } from "../../../../convex/schema";
import { cn } from "@repo/utils";
import { useCandidateAuthStore } from "@repo/auth";
import { useUIStore } from "../stores/uiStore";
import { getTemplate } from "../templates";
import { useMutation, useQuery } from "convex/react";
import { Mail, Palette } from "lucide-react";
import { api } from "../../../../convex/_generated/api";
import { useEffect, useState } from "react";

export function Home() {
  const { evaluationId, token, parcoursId } = useCandidateAuthStore();
  const openTutorial = useUIStore((state) => state.openTutorial);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sortByColor, setSortByColor] = useState(false);

  // Auto-open tutorial on first connection (tracked via localStorage per evaluation)
  useEffect(() => {
    if (!evaluationId) return;
    const key = `inbasket-tutorial-shown-${evaluationId}`;
    if (!localStorage.getItem(key)) {
      openTutorial();
      localStorage.setItem(key, "1");
    }
  }, [evaluationId, openTutorial]);

  const selectedMailKey = useQuery(
    api.inbasket.getSelectedMail,
    evaluationId && token && parcoursId ? { evaluationId, parcoursId, token } : "skip",
  );
  const setSelectedMailMutation = useMutation(api.inbasket.setSelectedMail);
  const searchQuery = useUIStore((state) => state.searchQuery);
  const mailFilter = useUIStore((state) => state.mailFilter);

  // Fetch evaluation data
  const evaluationResult = useQuery(
    api.evaluations.obtenirEvaluation,
    evaluationId && token && parcoursId ? { evaluationId, parcoursId, token } : "skip",
  );

  // Mutation for updating responses
  const mettreAJourReponses = useMutation(api.evaluations.mettreAJourReponses);
  const soumettreEvaluation = useMutation(api.evaluations.soumettreEvaluation);

  // Vérifie au chargement si tous les mails répondables sont déjà répondus
  // (cas d'une session reprise après que la soumission automatique n'a pas eu lieu)
  useEffect(() => {
    if (!evaluationResult || isBackendError(evaluationResult)) return;
    const eval_ = evaluationResult.evaluation as InbasketEvaluation;
    if (estEvaluationSoumise(eval_.statut)) return;
    if (showCompletionModal) return;

    const t = getTemplate(eval_.donnees.template);
    const tMails = getMailsComponentsForTemplate(t);
    const reponses = eval_.donnees.reponses as MailReponses;
    const visibleMails = getVisibleMails(tMails, reponses, t.mailTypeReponses);
    const mailsConditionnelsCaches = tMails.filter(
      (m) =>
        m.displayCondition &&
        !m.displayCondition({ reponses, templateMails: tMails, mailTypeReponses: t.mailTypeReponses }) &&
        t.mailTypeReponses[m.metadata.cleMail] !== "no_response",
    );
    const mailsARepondre = visibleMails.filter(
      (m) => t.mailTypeReponses[m.metadata.cleMail] !== "no_response",
    );
    const tousRepondus =
      mailsConditionnelsCaches.length === 0 &&
      mailsARepondre.length > 0 &&
      mailsARepondre.every(
        (m) => reponses[m.metadata.cleMail]?.statut === StatutMailEnum.REPONDU,
      );
    if (tousRepondus) setShowCompletionModal(true);
  }, [evaluationResult]); // eslint-disable-line react-hooks/exhaustive-deps

  // Loading state
  if (!evaluationResult) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <Loader />
      </div>
    );
  }

  // Error state
  if (isBackendError(evaluationResult)) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-destructive text-lg font-semibold">Erreur</p>
          <p className="text-muted-foreground">{evaluationResult.erreur}</p>
        </div>
      </div>
    );
  }

  const evaluation = evaluationResult.evaluation as InbasketEvaluation;
  const mailResponses = evaluation.donnees.reponses as MailReponses;

  // Get template configuration from evaluation
  const template = getTemplate(evaluation.donnees.template);

  // Get mails filtered by template, then by displayCondition, sorted by date desc
  const templateMails: MailComponent[] = getMailsComponentsForTemplate(template);
  const mails: MailComponent[] = getVisibleMails(templateMails, mailResponses, template.mailTypeReponses)
    .sort((a, b) => {
      // Mails with displayCondition (conditionally shown) always appear first
      const aConditional = a.displayCondition ? 1 : 0;
      const bConditional = b.displayCondition ? 1 : 0;
      if (aConditional !== bConditional) return bConditional - aConditional;
      return b.metadata.dateRelative - a.metadata.dateRelative;
    });

  // Get variablesData from template
  const variablesData = template.variablesData;

  // Filter mails based on search and filter criteria
  const filteredMails = mails.filter((mail) => {
    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (
        !mail.metadata.titre.toLowerCase().includes(q) &&
        !mail.metadata.emetteur.toLowerCase().includes(q) &&
        !mail.metadata.emetteurEntreprise?.toLowerCase().includes(q)
      ) {
        return false;
      }
    }

    // Category filter
    const response = mailResponses[mail.metadata.cleMail];
    switch (mailFilter) {
      case "important":
        return response?.important === true;
      case "sent":
        return response?.statut === StatutMailEnum.REPONDU;
      case "drafts":
        return response?.estEnBrouillon === true;
      default:
        return true;
    }
  });

  // Sort by color if active
  const displayedMails = sortByColor
    ? [...filteredMails].sort((a, b) => {
        const colorA = mailResponses[a.metadata.cleMail]?.couleur;
        const colorB = mailResponses[b.metadata.cleMail]?.couleur;
        const hasColorA = colorA && colorA !== "bg-white";
        const hasColorB = colorB && colorB !== "bg-white";
        if (!hasColorA && !hasColorB) return 0;
        if (!hasColorA) return 1;
        if (!hasColorB) return -1;
        return AVAILABLE_COLORS.indexOf(colorA as typeof AVAILABLE_COLORS[number]) -
               AVAILABLE_COLORS.indexOf(colorB as typeof AVAILABLE_COLORS[number]);
      })
    : filteredMails;

  // Selected mail
  const SelectedMailComponent = selectedMailKey
    ? mails.find((m) => m.metadata.cleMail === selectedMailKey)
    : null;

  // Callback to update mail response
  const handleUpdateMailResponse = async (
    mailKey: string,
    updates: Partial<(typeof mailResponses)[string]> & { reponse?: string },
  ) => {
    const currentResponse = mailResponses[mailKey] || {
      statut: StatutMailEnum.NON_LU,
    };

    const newReponses = {
      ...mailResponses,
      [mailKey]: {
        ...currentResponse,
        ...updates,
      },
    };

    const newDonnees = {
      ...evaluation.donnees,
      // selectedMailKey est toujours à jour contrairement à evaluation.donnees.mailSelectionneId
      // qui peut être périmé si getSelectedMail se met à jour avant obtenirEvaluation
      mailSelectionneId: selectedMailKey ?? evaluation.donnees.mailSelectionneId,
      reponses: newReponses,
    };

    await mettreAJourReponses({
      evaluationId,
      parcoursId,
      token,
      donnees: newDonnees,
    });

    // Auto-terminer si tous les mails visibles (calculés avec les nouvelles réponses) sont répondus
    // On utilise newReponses pour que les mails à displayCondition (ex: preparation_point_visio)
    // soient correctement inclus dans le calcul avant de soumettre
    const newVisibleMails = getVisibleMails(templateMails, newReponses as MailReponses, template.mailTypeReponses);
    const newMailsConditionnelsCaches = templateMails.filter(
      (m) =>
        m.displayCondition &&
        !m.displayCondition({ reponses: newReponses as MailReponses, templateMails, mailTypeReponses: template.mailTypeReponses }) &&
        template.mailTypeReponses[m.metadata.cleMail] !== "no_response",
    );
    const tousRepondus =
      newMailsConditionnelsCaches.length === 0 &&
      newVisibleMails
        .filter((mail) => template.mailTypeReponses[mail.metadata.cleMail] !== "no_response")
        .every(
          (mail) =>
            newReponses[mail.metadata.cleMail]?.statut === StatutMailEnum.REPONDU,
        );
    if (tousRepondus) {
      setShowCompletionModal(true);
    }
  };

  // Handle mail selection and mark as read
  const handleSelectMail = async (mailKey: string) => {
    const response = mailResponses[mailKey];
    if (!response || response.statut === StatutMailEnum.NON_LU) {
      // Combine selection + mark as read in a single mutation to avoid
      // the stale donnees race condition (handleUpdateMailResponse spreads
      // evaluation.donnees which still has the old mailSelectionneId)
      const currentResponse = mailResponses[mailKey] || {
        statut: StatutMailEnum.NON_LU,
      };
      await mettreAJourReponses({
        evaluationId,
        parcoursId,
        token,
        donnees: {
          ...evaluation.donnees,
          mailSelectionneId: mailKey,
          reponses: {
            ...mailResponses,
            [mailKey]: {
              ...currentResponse,
              statut: StatutMailEnum.LU,
            },
          },
        },
      });
    } else {
      await setSelectedMailMutation({
        evaluationId,
        parcoursId,
        token,
        mailId: mailKey,
      });
    }
  };

  const handleEnregistrer = async () => {
    setIsSubmitting(true);
    await soumettreEvaluation({
      evaluationId,
      parcoursId,
      token,
      donnees: evaluation.donnees,
    });
    setIsSubmitting(false);
    setShowCompletionModal(false);
  };

  console.log(SelectedMailComponent?.metadata.cleMail);

  return (
    <Layout
      evaluationId={evaluationId}
      token={token}
      evaluation={evaluation}
    >
      {() => (
        <div className="flex flex-1 overflow-hidden">
          {showCompletionModal && (
            <CompletionModal
              onEnregistrer={handleEnregistrer}
              isLoading={isSubmitting}
            />
          )}
          {/* Mail List */}
          <div
            className={cn(
              "flex w-full flex-col border-r lg:w-[500px]",
              SelectedMailComponent && "hidden lg:flex",
            )}
          >
            <div className="flex items-center justify-end border-b px-3 py-2">
              <Button
                variant={sortByColor ? "secondary" : "ghost"}
                size="sm"
                className="gap-1.5 text-xs"
                onClick={() => setSortByColor((v) => !v)}
              >
                <Palette className="h-3.5 w-3.5" />
                Trier par couleur
              </Button>
            </div>
            <ScrollArea className="flex-1">
              <div className="divide-y">
                {displayedMails.map((mail) => (
                  <MailRow
                    key={mail.metadata.cleMail}
                    mailMetadata={mail.metadata}
                    mailResponse={mailResponses[mail.metadata.cleMail]}
                    typeReponse={template.mailTypeReponses[mail.metadata.cleMail]}
                    isSelected={selectedMailKey === mail.metadata.cleMail}
                    onSelect={() => handleSelectMail(mail.metadata.cleMail)}
                  />
                ))}
                {displayedMails.length === 0 && (
                  <div className="p-6 text-center text-muted-foreground">
                    Aucun email trouvé
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Mail Detail */}
          {SelectedMailComponent ? (
            <MailDetail
              key={SelectedMailComponent.metadata.cleMail}
              CurrentMailComponent={SelectedMailComponent}
              mailResponse={
                mailResponses[SelectedMailComponent.metadata.cleMail]
              }
              variablesData={variablesData}
              onClose={() => setSelectedMailMutation({ evaluationId, parcoursId, token, mailId: undefined })}
              typeReponse={template.mailTypeReponses[SelectedMailComponent.metadata.cleMail]}
              isCompleted={estEvaluationSoumise(evaluation.statut)}
              onColorChange={(color) =>
                handleUpdateMailResponse(
                  SelectedMailComponent.metadata.cleMail,
                  {
                    couleur: color,
                  },
                )
              }
              onToggleImportant={() =>
                handleUpdateMailResponse(
                  SelectedMailComponent.metadata.cleMail,
                  {
                    important:
                      !mailResponses[SelectedMailComponent.metadata.cleMail]
                        ?.important,
                  },
                )
              }
              onSaveResponse={(reponse, transfertA) =>
                handleUpdateMailResponse(
                  SelectedMailComponent.metadata.cleMail,
                  {
                    reponse,
                    transfertA,
                    statut: StatutMailEnum.REPONDU,
                    estEnBrouillon: false,
                  },
                )
              }
              onSaveDraft={(reponse, transfertA) =>
                handleUpdateMailResponse(
                  SelectedMailComponent.metadata.cleMail,
                  {
                    reponse,
                    transfertA,
                    estEnBrouillon: true,
                  },
                )
              }
              onStartDraft={() =>
                handleUpdateMailResponse(
                  SelectedMailComponent.metadata.cleMail,
                  {
                    estEnBrouillon: true,
                  },
                )
              }
              onCancelDraft={() =>
                handleUpdateMailResponse(
                  SelectedMailComponent.metadata.cleMail,
                  {
                    estEnBrouillon: false,
                  },
                )
              }
              onStartEdit={() =>
                handleUpdateMailResponse(
                  SelectedMailComponent.metadata.cleMail,
                  {
                    estEnBrouillon: true,
                    statut: StatutMailEnum.LU,
                  },
                )
              }
            />
          ) : (
            <div className="hidden flex-1 items-center justify-center lg:flex">
              <div className="text-center text-muted-foreground">
                <Mail className="mx-auto h-12 w-12 mb-4" />
                <p>Sélectionnez un email pour le lire</p>
              </div>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}

export default Home;
